import { memoize } from "lodash";
import {
  isDefinedObject,
  isDefinedString,
  isPositiveNumber,
  reduce
} from "et3-type-guards";
import {
  E_KHATTABAT_ASSIGN_CLIENT_USER_TYPE,
  E_KHATTABAT_CONVERSATION_TYPE,
  Gender,
  TGender
} from "../../Types";
import {
  DEFAULT_NOTIFICATION_DATA,
  TNotification,
  TNotificationData,
  TNotificationPayloadMap
} from "./types";
import { E_NOTIFICATION_TYPE } from "./constants";
import { getNotificationSoundStore } from "./notification-sound-store";
import getCorePersonalStore from "../core/core-personal-store";
import getCoreHistoryStore from "../core/core-history-store";
import getCoreTimingStore from "../core/core-timing-store";
import getKhattabat102Store from "../khattabat-stores/102-stores";
import getKhattabatSharedMessagingStore from "../khattabat-stores/messaging-stores/conversation-messaging-store";
import getDashBoardStore from "../dashboard-store";
import getKhattabat101CGeneralStore from "../khattabat-stores/101c-stores/101c-general-store";
import getKhattabat101CStore from "../khattabat-stores/101c-stores";
import getKhattabat101MatchStore from "../khattabat-stores/101m-stores";
import getKhattabat101MGeneralStore from "../khattabat-stores/101m-stores/general-101m-store";
import getKhattabat101MessagingStore from "../khattabat-stores/messaging-stores/101-messaging-store";
import getKhattabatRemunerationStore from "../khattabat-stores/khattabat-remuneration-stores";
import getKhattabat101CMessagingStore from "../khattabat-stores/101c-stores/101c-messaging-store";

class NotificationStore {
  isNotification = (
    notification: TNotificationData
  ): notification is TNotification => {
    if (!isDefinedObject(notification)) {
      return false;
    }

    const notificationType: string = reduce(
      () => notification.notificationType,
      ""
    );
    const notificationPayload: object = reduce(() => notification.payload, {});

    return (
      isDefinedString(notificationType) && isDefinedObject(notificationPayload)
    );
  };

  async handleFirebaseNotificationBody({ body }: { body: string }) {
    if (!isDefinedString(body)) {
      return;
    }

    const parsedBody: TNotificationData = reduce(
      () => JSON.parse(body),
      DEFAULT_NOTIFICATION_DATA
    );

    if (!this.isNotification(parsedBody)) {
      return;
    }

    switch (parsedBody.notificationType) {
      case E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE:
        await this.handleConversationMessage(parsedBody);
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE_REACTION:
        await this.handleConversationMessageReaction(parsedBody);
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_NEW_CLIENT_TO_BE_ASSIGNED:
        this.handleNewClientToBeAssigned(parsedBody);
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_NEW_CLIENT_ASSIGNED:
        this.handleNewClientAssigned(parsedBody);
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_CLIENT_PAID_PHASE:
        if (!this.handleClientPaidPhase(parsedBody)) {
          return;
        }
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_CLIENT_SUBSCRIPTION_RENEWED:
        if (!this.handleClientSubscriptionRenewed(parsedBody)) {
          return;
        }
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_REMUNERATION_UPDATE:
        await this.handleRemunerationUpdate();
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_DELETED:
        if (!this.handleConversationDeleted(parsedBody)) {
          return;
        }
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_EXITED:
        if (!(await this.handleConversationExited(parsedBody))) {
          return;
        }
        break;

      case E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_ACCEPTED:
        await this.handleConversationAccepted();
        break;

      default:
        return;
    }

    const dataOnly: boolean = reduce(() => parsedBody.dataOnly, false);

    if (!dataOnly) {
      getNotificationSoundStore().playNotificationSound();
    }
  }

  private handleConversationMessage = async ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE];
  }) => {
    const conversationType: string = reduce(
      () => payload.conversationType,
      E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO
    );
    const clientUserID = reduce(() => payload.clientUserID, 0);

    switch (conversationType) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_C:
        if (isPositiveNumber(clientUserID)) {
          const isClientLoaded = getKhattabat101CGeneralStore()
            .allAvailableClientsIds.get()
            .includes(clientUserID);
          if (isClientLoaded) {
            getKhattabat101CMessagingStore(
              clientUserID
            ).appendReceivedMessageFromNotification({ payload });
          } else {
            await getDashBoardStore().getKhattabatDashboardData();
          }
        }
        break;

      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO:
        const wasMessageAppended = getKhattabat102Store(
          reduce(() => payload.conversationID, "")
        ).appendReceivedMessageFromNotification({ payload });
        // false means the 102 chat is not loaded, so the message could not be
        // appended to it; refresh the page data instead so lists/counters update
        if (!wasMessageAppended) {
          await getCoreTimingStore().refreshKhattabatPageData(
            getCoreHistoryStore().getPageName()
          );
        }
        break;

      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M:
        const sharedStore = getKhattabatSharedMessagingStore(
          reduce(() => payload.conversationID, "")
        );
        if (
          sharedStore.conversationType.get() ===
          E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M
        ) {
          const payloadGender = reduce(() => payload.gender, "");
          const knownMatchGender: TGender = reduce(
            () =>
              sharedStore.messages
                .get()
                .find(
                  (conversationMessage) =>
                    conversationMessage.isClientMessage === 0 &&
                    conversationMessage.isKhattabaMessage === 0 &&
                    isDefinedString(conversationMessage.senderGender)
                ).senderGender,
            Gender.Male
          );
          sharedStore.appendReceivedMessage({
            message: reduce(() => payload.message, ""),
            messageType: reduce(() => payload.messageType, "text"),
            senderGender: isDefinedString(payloadGender)
              ? payloadGender
              : knownMatchGender,
            senderUUID: reduce(() => payload.senderUUID, "")
          });
        } else {
          await getCoreTimingStore().refreshKhattabatPageData(
            getCoreHistoryStore().getPageName()
          );
        }
        break;
    }
  };

  private handleConversationMessageReaction = async ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_MESSAGE_REACTION];
  }) => {
    await getKhattabat102Store(
      reduce(() => payload.conversationID, "")
    ).handleReactionNotification({ payload });
  };

  private handleNewClientToBeAssigned = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_NEW_CLIENT_TO_BE_ASSIGNED];
  }) => {
    getDashBoardStore().incrementUnassignedNewClients(
      reduce(() => payload.userType, E_KHATTABAT_ASSIGN_CLIENT_USER_TYPE.REAL)
    );
  };

  private handleNewClientAssigned = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_NEW_CLIENT_ASSIGNED];
  }) => {
    const isAssignedByCurrentAdmin =
      reduce(() => payload.assignedByAdminID, 0) ===
      getCorePersonalStore().user.get().id;
    if (isAssignedByCurrentAdmin) {
      return;
    }
    getDashBoardStore().decrementUnassignedNewClientsByType(
      reduce(() => payload.userType, E_KHATTABAT_ASSIGN_CLIENT_USER_TYPE.REAL)
    );
  };

  private isClientLoaded = (clientUserID: number): boolean => {
    return getKhattabat101CGeneralStore()
      .allAvailableClientsIds.get()
      .includes(clientUserID);
  };

  private handleClientPaidPhase = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CLIENT_PAID_PHASE];
  }): boolean => {
    const clientUserID = reduce(() => payload.clientUserID, 0);
    if (!this.isClientLoaded(clientUserID)) {
      return false;
    }
    getKhattabat101CStore(clientUserID).applyPaidPhaseFromNotification({
      isIntroductory: reduce(() => payload.isIntroductory, false)
    });
    return true;
  };

  private handleClientSubscriptionRenewed = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CLIENT_SUBSCRIPTION_RENEWED];
  }): boolean => {
    const clientUserID = reduce(() => payload.clientUserID, 0);
    if (!this.isClientLoaded(clientUserID)) {
      return false;
    }
    getKhattabat101CStore(
      clientUserID
    ).applySubscriptionRenewalFromNotification();
    return true;
  };

  private handleRemunerationUpdate = async () => {
    await getDashBoardStore().getKhattabatDashboardData();
    const remunerationStore = getKhattabatRemunerationStore();
    if (remunerationStore.khattabatAdminsRemunerations.get().length > 0) {
      await remunerationStore.getKhattabatRemunerationsDatabase();
    }
  };

  private handleConversationDeleted = ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_DELETED];
  }): boolean => {
    const conversationType: string = reduce(
      () => payload.conversationType,
      E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO
    );
    if (conversationType !== E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M) {
      return false;
    }

    const conversationID = reduce(() => payload.conversationID, "");
    const matchID =
      getKhattabat101MessagingStore(conversationID).receiverID.get();
    const isConversationLoaded =
      getKhattabatSharedMessagingStore(
        conversationID
      ).conversationType.get() === E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M &&
      isPositiveNumber(matchID);
    if (!isConversationLoaded) {
      return false;
    }
    getKhattabat101MatchStore(matchID).setConversationDeletionState(true);
    return true;
  };

  private handleConversationExited = async ({
    payload
  }: {
    payload: TNotificationPayloadMap[E_NOTIFICATION_TYPE.KHATTABAT_CONVERSATION_EXITED];
  }): Promise<boolean> => {
    const wasExitApplied = getKhattabat102Store(
      reduce(() => payload.conversationID, "")
    ).applyExitFromNotification({ payload });
    if (!wasExitApplied) {
      await getCoreTimingStore().refreshKhattabatPageData(
        getCoreHistoryStore().getPageName()
      );
    }
    return wasExitApplied;
  };

  private handleConversationAccepted = async () => {
    await getKhattabat101MGeneralStore().getUnreadMatchesConversations();
    getKhattabat101MGeneralStore().updateClientOpenedOneOOneMConversations();
  };
}
export const getNotificationStore = memoize(
  () => {
    return new NotificationStore();
  },
  () => 1
);
